import React from 'react';
import { Box, Typography, Paper, ButtonBase } from '@mui/material';
import type { Employee, EmployeeMonthlyInfo } from '../../types/employee';
import { groupEmployeesByPayType } from '../../lib/payroll/display-helper';
import { netPayFromMonthlyInfo } from '../../lib/payroll/net-pay';
import { isDailyWorkerPay, isBusinessServicePay, resolvePayType } from '../../types/employee';
import { formatNumber } from '../../utils/formatters';
import PayTypeBadge from './PayTypeBadge';
import SectionHeader from '../SectionHeader';

interface EmployeeNonRegularCardsProps {
  employees: Employee[];
  monthlyInfoList: EmployeeMonthlyInfo[];
  onCardClick: (employee: Employee) => void;
}

const EmployeeNonRegularCards: React.FC<EmployeeNonRegularCardsProps> = ({
  employees,
  monthlyInfoList,
  onCardClick,
}) => {
  const infoMap = new Map(monthlyInfoList.map((i) => [i.employee.id, i]));
  const sections = groupEmployeesByPayType(employees).filter(
    (s) => s.key === 'dailyWorker' || s.key === 'businessService',
  );

  if (sections.length === 0) return null;

  return (
    <Box sx={{ mb: 3 }}>
      {sections.map((section) => (
        <Box key={section.key} sx={{ mb: 2.5 }}>
          <SectionHeader eyebrow="급여 유형" title={section.label} size="card" />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {section.employees.map((emp) => {
              const info = infoMap.get(emp.id);
              const workDays = info?.workDays ?? 0;
              const net = info ? netPayFromMonthlyInfo(info) : 0;
              const payType = resolvePayType(emp.payType, emp.isHourly);

              return (
                <ButtonBase
                  key={emp.id}
                  onClick={() => onCardClick(emp)}
                  sx={{
                    width: '100%',
                    textAlign: 'left',
                    borderRadius: 2,
                    display: 'block',
                  }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      border: '1px solid var(--border-default)',
                      borderRadius: 2,
                      '&:hover': { borderColor: 'var(--primary)', bgcolor: 'var(--bg-green)' },
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '1.0625rem', color: 'var(--fg-strong)' }}>
                        {emp.name}
                      </Typography>
                      <PayTypeBadge payType={payType} />
                    </Box>
                    <Typography variant="body2" sx={{ color: 'var(--fg-secondary)' }}>
                      {workDays > 0 ? (
                        <>
                          근무 {workDays}일 · 실수령{' '}
                          <Typography component="span" className="ds-num-md" sx={{ color: 'var(--primary)', fontWeight: 700 }}>
                            {formatNumber(net)}원
                          </Typography>
                        </>
                      ) : (
                        <>
                          {isDailyWorkerPay(emp) ? '일당' : '건별'}{' '}
                          {formatNumber(emp.dailyRate ?? 0)}원 · 이번 달 근무 없음
                        </>
                      )}
                    </Typography>
                    {isDailyWorkerPay(emp) && workDays > 0 && info && info.totalEmployeeInsurance > 0 && (
                      <Typography variant="caption" sx={{ color: 'var(--fg-tertiary)', display: 'block', mt: 0.5 }}>
                        고용보험(근로자) {formatNumber(info.totalEmployeeInsurance)}원 공제
                      </Typography>
                    )}
                  </Paper>
                </ButtonBase>
              );
            })}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default EmployeeNonRegularCards;
